import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { computeVehicleCheckDue } from "@/lib/vehicleCheckDue";
import { loadCheckItemTypeRestrictions, checkItemAppliesToType, type CheckItemTypeRestrictions } from "@/lib/vehicleCheckItems";
import { recordInspectionOdometer } from "@/lib/vehicleOdometer";
import { formatDateTime } from "@/lib/dates";

type Status = "OK" | "Needs Attention" | "Needs Fixing Soon" | "Unsafe";

type DueResult = {
  is_due: boolean;
  completed_shifts_since_last_check: number;
  days_since_last_check: number | null;
  last_inspection_shift_date: string | null;
  threshold: number;
  interval_days: number;
  reason: string;
};

type PriorItemResult = { status: Status; note: string };

type ActiveShift = {
  id: string;
  shift_date: string;
  start_time: string;
  shift_status: "active" | "completed";
};

type VehicleRow = {
  vehicle_number: string;
  make: string | null;
  model: string | null;
  vehicle_type: string | null;
  status: string | null;
};

type ChecklistItem = {
  category: string;
  item_key: string;
  label: string;
  status: Status;
  note: string;
};

type DriverRow = {
  driver_name: string;
  AdminRights: string | null;
};

type CaptureKey =
  | "exterior_front"
  | "exterior_rear"
  | "exterior_driver"
  | "exterior_passenger"
  | "oil_dipstick"
  | "odometer";

type OverlayView = "front" | "rear" | "driver" | "passenger";

const STATUS_OPTIONS: { value: Status; label: string }[] = [
  { value: "OK", label: "OK" },
  { value: "Needs Attention", label: "Needs Attention" },
  { value: "Needs Fixing Soon", label: "Needs Fixing Soon" },
  { value: "Unsafe", label: "Unsafe / Not OK" },
];

const STATUS_COLOR: Record<Status, string> = {
  OK: "#22c55e",
  "Needs Attention": "#eab308",
  "Needs Fixing Soon": "#f97316",
  Unsafe: "#ef4444",
};

const BUCKET_VEHICLE_INSPECTIONS = "vehicle-inspections";
const SETTINGS_THRESHOLD_NAME = "fleet_vehicle_check_shift_threshold";
const SETTINGS_INTERVAL_DAYS_NAME = "fleet_vehicle_check_interval_days";
const SETTINGS_REQUIRE_ODOMETER_NAME = "fleet_vehicle_check_require_odometer";

const EXTERIOR_KEYS: CaptureKey[] = ["exterior_front", "exterior_rear", "exterior_driver", "exterior_passenger"];
const VIEW_LABELS: Record<CaptureKey, string> = {
  exterior_front: "FRONT",
  exterior_rear: "REAR",
  exterior_driver: "DRIVER",
  exterior_passenger: "PASS",
  oil_dipstick: "OIL",
  odometer: "ODO",
};

function isIOSDevice() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua);
}

function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  const navAny = window.navigator as any;
  const iosStandalone = !!navAny.standalone;
  const displayModeStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
  return iosStandalone || displayModeStandalone;
}

function safePathPart(s: string) {
  return s.replace(/[^a-zA-Z0-9-_]+/g, "_");
}

function computeOverallStatus(items: ChecklistItem[]): Status {
  const order: Status[] = ["OK", "Needs Attention", "Needs Fixing Soon", "Unsafe"];
  let idx = 0;
  for (const it of items) {
    const i = order.indexOf(it.status);
    if (i > idx) idx = i;
  }
  return order[idx];
}

async function blobFromFile(file: File): Promise<Blob> {
  return file;
}

function isExteriorKey(k: CaptureKey) {
  return (
    k === "exterior_front" ||
    k === "exterior_rear" ||
    k === "exterior_driver" ||
    k === "exterior_passenger"
  );
}

function captureKeyToView(k: CaptureKey): OverlayView | null {
  if (k === "exterior_front") return "front";
  if (k === "exterior_rear") return "rear";
  if (k === "exterior_driver") return "driver";
  if (k === "exterior_passenger") return "passenger";
  return null;
}

/**
 * iOS-safe aspect container:
 * instead of CSS aspect-ratio in fixed modals, we use padding-top.
 * aspect string like "9 / 16" or "16 / 9" etc.
 */
function aspectToPaddingTop(aspect: string) {
  const parts = aspect.split("/").map((s) => parseFloat(s.trim()));
  const w = parts[0] || 9;
  const h = parts[1] || 16;
  return `${(h / w) * 100}%`;
}

/**
 * Overlays stored as PNG with transparency in vehicle-inspections bucket.
 * e.g. sedan/sedan_front.png
 */
function getOverlayStoragePath(overlayAssetKey: string, view: OverlayView) {
  return `${overlayAssetKey}/${overlayAssetKey}_${view}.png`;
}

/**
 * Frame specs per capture
 */
function getFrameSpec(k: CaptureKey) {
  if (k === "exterior_front" || k === "exterior_rear") {
    return { aspect: "9 / 16", label: "Portrait (9:16)" };
  }
  if (k === "exterior_driver" || k === "exterior_passenger") {
    return { aspect: "16 / 9", label: "Landscape (16:9)" };
  }
  return { aspect: "3 / 4", label: "Portrait (3:4)" };
}

function makeCalKey(vehicleType: string, view: OverlayView) {
  return `overlay_cal_${vehicleType}_${view}`;
}

interface VehicleInspectionProps {
  onInspectionSubmitted?: () => void;
  /**
   * Passed by DriverDashboard so this still resolves the right driver during
   * admin impersonation (DriverViewModal.tsx never touches localStorage --
   * it hands driverName down as a prop instead). Falls back to the
   * currentUser localStorage lookup for the standalone /vehicle-inspection
   * route in App.tsx, which renders this with no props at all.
   */
  driverName?: string;
}

export default function VehicleInspection({ onInspectionSubmitted, driverName: driverNameProp }: VehicleInspectionProps = {}) {
  // Get driver name from the currentUser stored in localStorage (set during login)
  const driverNameFromStorage = (() => {
    if (typeof window === "undefined") return "";
    try {
      const storedUser = localStorage.getItem("currentUser");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        return parsed.driver_name || "";
      }
    } catch {
      // ignore parse errors
    }
    return "";
  })();
  const driverName = driverNameProp || driverNameFromStorage;


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");

  const [activeShift, setActiveShift] = useState<ActiveShift | null>(null);

  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [selectedVehicleNumber, setSelectedVehicleNumber] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleRow | null>(null);
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  // Today's rostered vehicle(s), same lookup DriverFuelCard.tsx uses --
  // pre-selects the dropdown below instead of making a driver hunt through
  // the full fleet list for the vehicle they're already in.
  const [todaysVehicleNumbers, setTodaysVehicleNumbers] = useState<string[]>([]);

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  // Which vehicle types each check item applies to (e.g. a hoist-photo item
  // scoped to just Wheelchair Van) -- see VehicleCheckItemsSettings.tsx for
  // where this is configured. An item with no rows applies to every type.
  const [checkItemTypeRestrictions, setCheckItemTypeRestrictions] = useState<CheckItemTypeRestrictions>(new Map());
  useEffect(() => {
    loadCheckItemTypeRestrictions().then(setCheckItemTypeRestrictions);
  }, []);

  const [threshold, setThreshold] = useState<number>(3);
  const [intervalDays, setIntervalDays] = useState<number>(3);
  const [due, setDue] = useState<DueResult | null>(null);

  // Defaults to required -- the odometer photo already existed but nothing
  // ever recorded the actual number, which is how a vehicle got 4,000km
  // over its RUC limit before anyone noticed. Overridden below if an admin
  // has explicitly turned this off in Settings -> Fleet Defaults -> Fleet Checks.
  const [requireOdometerReading, setRequireOdometerReading] = useState<boolean>(true);
  const [odometerReading, setOdometerReading] = useState("");

  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [inspectionId, setInspectionId] = useState<string>("");

  const [photoExteriorFront, setPhotoExteriorFront] = useState("");
  const [photoExteriorRear, setPhotoExteriorRear] = useState("");
  const [photoExteriorDriver, setPhotoExteriorDriver] = useState("");
  const [photoExteriorPassenger, setPhotoExteriorPassenger] = useState("");

  const [photoOilDipstick, setPhotoOilDipstick] = useState("");
  const [photoOdoOptional, setPhotoOdoOptional] = useState("");

  const [generalNotes, setGeneralNotes] = useState("");

  // Loaded from vehicle_check_items (admin-configurable in Settings ->
  // Fleet Defaults -> Fleet Checks) instead of a hardcoded list -- see
  // VehicleCheckItemsSettings.tsx. Falls back to an empty list rather than
  // hardcoded items if the load fails, so a driver never gets a stale
  // checklist silently out of step with what admins actually configured.
  const [baseChecklist, setBaseChecklist] = useState<ChecklistItem[]>([]);

  useEffect(() => {
    async function loadChecklist() {
      const { data, error } = await supabase
        .from("vehicle_check_items")
        .select("category, item_key, label")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) {
        console.error("Error loading vehicle check items:", error);
        return;
      }

      setBaseChecklist(
        (data || []).map((item) => ({
          category: item.category,
          item_key: item.item_key,
          label: item.label,
          status: "OK" as Status,
          note: "",
        }))
      );
    }

    loadChecklist();
  }, []);

  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);

  // Any item marked something other than "OK" on the vehicle's last
  // inspection, keyed by item_key -- shown as a small hint under the
  // matching item in Step 3 so a fixed issue doesn't just quietly default
  // back to OK unnoticed (see startInspection).
  const [priorItemResults, setPriorItemResults] = useState<Record<string, PriorItemResult>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Camera modal state
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [isStreamActive, setIsStreamActive] = useState(false); // ✅ separates stream "alive" vs UI ready
  const [cameraError, setCameraError] = useState<string>("");
  const [cameraFallback, setCameraFallback] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [currentCaptureKey, setCurrentCaptureKey] = useState<CaptureKey>("exterior_front");

  // Success animation state
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // ✅ IMPORTANT: video must exist regardless of cameraReady (same as your working demo)
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Guide controls
  const [guideVisible, setGuideVisible] = useState(true);
  const [guideOpacity, setGuideOpacity] = useState<number>(0.55);

  // Admin + calibration
  const [isAdmin, setIsAdmin] = useState(false);

  const [calibration, setCalibration] = useState<Record<OverlayView, { x: number; y: number; scale: number }>>({
    front: { x: 0, y: 0, scale: 1 },
    rear: { x: 0, y: 0, scale: 1 },
    driver: { x: 0, y: 0, scale: 1 },
    passenger: { x: 0, y: 0, scale: 1 },
  });

  const frameSpec = useMemo(() => getFrameSpec(currentCaptureKey), [currentCaptureKey]);

  // Helper to get photo state by key
  const getPhotoByKey = useCallback((key: CaptureKey): string => {
    const photoMap: Record<CaptureKey, string> = {
      exterior_front: photoExteriorFront,
      exterior_rear: photoExteriorRear,
      exterior_driver: photoExteriorDriver,
      exterior_passenger: photoExteriorPassenger,
      oil_dipstick: photoOilDipstick,
      odometer: photoOdoOptional,
    };
    return photoMap[key];
  }, [photoExteriorFront, photoExteriorRear, photoExteriorDriver, photoExteriorPassenger, photoOilDipstick, photoOdoOptional]);

  // Count captured exterior photos
  const capturedExteriorCount = useMemo(() => {
    return [photoExteriorFront, photoExteriorRear, photoExteriorDriver, photoExteriorPassenger].filter(Boolean).length;
  }, [photoExteriorFront, photoExteriorRear, photoExteriorDriver, photoExteriorPassenger]);

  // Init load
  useEffect(() => {
    async function init() {
      setLoading(true);
      setError("");

      try {
        const { data: vehicleData, error: vehicleError } = await supabase
          .from("vehicles")
          .select("vehicle_number, make, model, vehicle_type, status")
          .order("vehicle_number");
        if (vehicleError) throw vehicleError;
        setVehicles(vehicleData || []);

        const { data: settingRows, error: settingError } = await supabase
          .from("settings")
          .select("name, value")
          .in("name", [SETTINGS_THRESHOLD_NAME, SETTINGS_INTERVAL_DAYS_NAME, SETTINGS_REQUIRE_ODOMETER_NAME]);

        if (!settingError && settingRows) {
          const thresholdRow = settingRows.find((s) => s.name === SETTINGS_THRESHOLD_NAME);
          if (thresholdRow?.value) {
            const n = parseInt(String(thresholdRow.value), 10);
            if (!Number.isNaN(n) && n > 0) setThreshold(n);
          }

          const intervalRow = settingRows.find((s) => s.name === SETTINGS_INTERVAL_DAYS_NAME);
          if (intervalRow?.value) {
            const n = parseInt(String(intervalRow.value), 10);
            if (!Number.isNaN(n) && n > 0) setIntervalDays(n);
          }

          const requireOdometerRow = settingRows.find((s) => s.name === SETTINGS_REQUIRE_ODOMETER_NAME);
          if (requireOdometerRow?.value) setRequireOdometerReading(requireOdometerRow.value === "true");
        }

        if (driverName) {
          const { data: shiftData, error: shiftError } = await supabase
            .from("driver_shifts")
            .select("id, shift_date, start_time, shift_status")
            .eq("driver_name", driverName)
            .eq("shift_status", "active")
            .order("shift_date", { ascending: false })
            .limit(1)
            .maybeSingle();
          if (!shiftError && shiftData) setActiveShift(shiftData as ActiveShift);

          const { data: driverRow, error: driverErr } = await supabase
            .from("drivers")
            .select("driver_name, AdminRights")
            .eq("driver_name", driverName)
            .maybeSingle();

          if (!driverErr && driverRow) {
            const dr = driverRow as DriverRow;
            const flag = (dr.AdminRights || "").toLowerCase();
            setIsAdmin(flag === "yes" || flag === "admin" || flag === "true");
          }

          const todayStr = format(new Date(), "yyyy-MM-dd");
          const { data: rosterShifts, error: rosterError } = await supabase
            .from("vw_roster_shifts_enriched")
            .select("vehicle_number")
            .eq("driver_name", driverName)
            .eq("shift_date", todayStr)
            .eq("status", "published")
            .not("vehicle_number", "is", null);

          if (!rosterError) {
            setTodaysVehicleNumbers(
              Array.from(new Set((rosterShifts || []).map((s) => s.vehicle_number as string)))
            );
          }
        }
      } catch (err: any) {
        setError(err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    }

    init();
  }, [driverName]);

  // Pre-select today's rostered vehicle once both lists are in, instead of
  // leaving a driver to hunt through the full fleet dropdown for the one
  // they're already in. Only fires once (guarded by selectedVehicleNumber
  // being empty) so it never fights a driver who's deliberately picked a
  // different vehicle -- e.g. rostering data being stale, or a mid-day swap.
  useEffect(() => {
    if (selectedVehicleNumber || todaysVehicleNumbers.length === 0 || vehicles.length === 0) return;
    const knownVehicle = todaysVehicleNumbers.find((vn) => vehicles.some((v) => v.vehicle_number === vn));
    if (knownVehicle) setSelectedVehicleNumber(knownVehicle);
  }, [todaysVehicleNumbers, vehicles, selectedVehicleNumber]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Load calibration from localStorage when vehicle type changes
  useEffect(() => {
    if (!selectedVehicle?.vehicle_type) return;
    const vt = selectedVehicle.vehicle_type;

    try {
      const views: OverlayView[] = ["front", "rear", "driver", "passenger"];
      const next: any = {};
      for (const v of views) {
        const key = makeCalKey(vt, v);
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          next[v] = {
            x: Number(parsed.x ?? 0),
            y: Number(parsed.y ?? 0),
            scale: Number(parsed.scale ?? 1),
          };
        } else {
          next[v] = { x: 0, y: 0, scale: 1 };
        }
      }
      setCalibration(next);
    } catch {
      // ignore
    }
  }, [selectedVehicle?.vehicle_type]);

  // Due check
  useEffect(() => {
    async function checkDue() {
      if (!selectedVehicleNumber) {
        setDue(null);
        setSelectedVehicle(null);
        return;
      }

      const vehicle = vehicles.find((v) => v.vehicle_number === selectedVehicleNumber);
      setSelectedVehicle(vehicle || null);

      const result = await computeVehicleCheckDue(selectedVehicleNumber, intervalDays, threshold);

      setDue({
        is_due: result.isDue,
        completed_shifts_since_last_check: result.completedShiftsSinceLastCheck,
        days_since_last_check: result.daysSinceLastCheck,
        last_inspection_shift_date: result.lastInspectionAt,
        threshold,
        interval_days: intervalDays,
        reason: result.reason,
      });
    }

    checkDue();
  }, [selectedVehicleNumber, vehicles, threshold, intervalDays]);

  async function startInspection() {
    if (!selectedVehicle) return;
    setError("");

    // Fetch the vehicle's most recent PRIOR inspection's items before
    // creating the new one, so a driver can see what was flagged last time
    // (see priorItemResults) without it affecting the new checklist's
    // starting values -- everything still defaults to OK as before.
    const { data: lastInspection } = await supabase
      .from("vehicle_inspections")
      .select("id")
      .eq("vehicle_number", selectedVehicle.vehicle_number)
      .not("completed_at", "is", null)
      .order("completed_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (lastInspection?.id) {
      const { data: priorItems } = await supabase
        .from("vehicle_inspection_items")
        .select("item_key, status, note")
        .eq("inspection_id", lastInspection.id);

      const priorMap: Record<string, PriorItemResult> = {};
      (priorItems || []).forEach((item) => {
        if (item.status && item.status !== "OK") {
          priorMap[item.item_key] = { status: item.status as Status, note: item.note || "" };
        }
      });
      setPriorItemResults(priorMap);
    } else {
      setPriorItemResults({});
    }

    const { data: newInspection, error: createError } = await supabase
      .from("vehicle_inspections")
      .insert({
        vehicle_number: selectedVehicle.vehicle_number,
        driver_name: driverName || "Unknown",
        overall_status: "OK",
        notes: "",
      })
      .select("id")
      .single();

    if (createError) {
      setError("Failed to create inspection: " + createError.message);
      return;
    }

    setInspectionId(newInspection.id);
    setChecklist(
      baseChecklist.filter((item) =>
        checkItemAppliesToType(item.item_key, selectedVehicle.vehicle_type, checkItemTypeRestrictions)
      )
    );

    setPhotoExteriorFront("");
    setPhotoExteriorRear("");
    setPhotoExteriorDriver("");
    setPhotoExteriorPassenger("");
    setPhotoOilDipstick("");
    setPhotoOdoOptional("");
    setOdometerReading("");
    setGeneralNotes("");

    setStep(1);
    setWizardOpen(true);
    setSubmitSuccess(false);
  }

  function getPublicUrl(path: string): string {
    if (!path) return "";
    const { data } = supabase.storage.from(BUCKET_VEHICLE_INSPECTIONS).getPublicUrl(path);
    return data?.publicUrl || "";
  }

  function getOverlayAssetKey(vehicleType: string | null | undefined): string | null {
    if (!vehicleType) return null;
    return vehicleTypes.find((t) => t.vehicle_type === vehicleType)?.overlay_asset_key || null;
  }

  function getOverlayUrl(view: OverlayView): string {
    const overlayAssetKey = getOverlayAssetKey(selectedVehicle?.vehicle_type);
    if (!overlayAssetKey) return "";
    const path = getOverlayStoragePath(overlayAssetKey, view);
    return getPublicUrl(path);
  }

  async function uploadBlobToStorage(blob: Blob, photoType: string): Promise<string> {
    if (!inspectionId) throw new Error("Inspection not initialized yet.");

    const path = `inspections/${inspectionId}/${safePathPart(photoType)}_${Date.now()}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET_VEHICLE_INSPECTIONS)
      .upload(path, blob, { upsert: true, contentType: "image/jpeg" });

    if (uploadError) throw new Error("Upload failed: " + uploadError.message);

    return path;
  }

  async function upsertPhotoRow(photoType: string, storagePath: string) {
    const category = photoType.startsWith("exterior_")
      ? "Exterior"
      : photoType === "oil_dipstick" || photoType === "odometer"
      ? "Engine"
      : "General";

    const { error: dbError } = await supabase
      .from("vehicle_inspection_photos")
      .upsert(
        {
          inspection_id: inspectionId,
          category,
          photo_type: photoType,
          storage_path: storagePath,
        },
        { onConflict: "inspection_id,category,photo_type" }
      );

    if (dbError) throw new Error("DB insert failed: " + dbError.message);
  }

  // --- Camera modal controls ---
  async function openCameraFor(photoKey: CaptureKey) {
    setCameraError("");
    setCameraReady(false);
    setIsStreamActive(false);
    setCameraFallback(false);
    setCurrentCaptureKey(photoKey);
    setCameraOpen(true);
    setShowSuccess(false);

    if (isIOSDevice()) {
      setCameraError(
        isStandalonePWA()
          ? 'iPhone tip: Live preview can be black in PWA. If that happens, use "Phone Camera".'
          : 'iPhone tip: If the live preview is black, use "Phone Camera".'
      );
    }

  }

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      // @ts-ignore
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
    setIsStreamActive(false);
  }, []);

  function closeCameraModal() {
    stopCamera();
    setCameraOpen(false);
    setShowSuccess(false);
  }

  // ✅ wait until video has real dimensions (prevents "authorized but blank")
  async function waitForVideoDimensions(video: HTMLVideoElement, timeoutMs = 2500) {
    const start = performance.now();
    return await new Promise<boolean>((resolve) => {
      const tick = () => {
        const w = video.videoWidth || 0;
        const h = video.videoHeight || 0;
        if (w > 0 && h > 0) return resolve(true);
        if (performance.now() - start > timeoutMs) return resolve(false);
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  async function enableCamera() {
    setCameraError("");
    setCameraReady(false);
    setIsStreamActive(false);
    setCameraFallback(false);

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Camera is not supported in this browser.");
      }

      stopCamera();

      // Request rear camera (environment-facing) for vehicle photos
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: { ideal: "environment" }  // Use rear camera
        },
        audio: false,
      });

      streamRef.current = stream;

      const video = videoRef.current;
      if (!video) throw new Error("Video element not available (ref not mounted).");

      video.setAttribute("playsinline", "true");
      video.muted = true;
      video.autoplay = true;

      // @ts-ignore
      video.srcObject = stream;

      // Try to play (iOS sometimes needs a beat)
      try {
        await video.play();
      } catch {
        // If autoplay is blocked, it may still show after user interaction; keep going.
      }

      // Give the browser a moment to paint frames
      const ok = await waitForVideoDimensions(video, 3000);

      if (!ok) {
        setCameraFallback(true);
        setCameraError("Live camera preview isn't available on this device/browser. Use Phone Camera instead.");
        setCameraReady(false);
        setIsStreamActive(false);
        stopCamera();
        return;
      }

      setIsStreamActive(true);
      setCameraReady(true);
    } catch (e: any) {
      setCameraFallback(true);
      setCameraError(e?.message || "Camera permission was denied or not available.");
      setCameraReady(false);
      setIsStreamActive(false);
      stopCamera();
    }
  }

  // Function to handle successful capture and auto-advance
  const handleCaptureSuccess = useCallback((capturedKey: CaptureKey, storagePath: string) => {
    // Update the photo state
    if (capturedKey === "exterior_front") setPhotoExteriorFront(storagePath);
    if (capturedKey === "exterior_rear") setPhotoExteriorRear(storagePath);
    if (capturedKey === "exterior_driver") setPhotoExteriorDriver(storagePath);
    if (capturedKey === "exterior_passenger") setPhotoExteriorPassenger(storagePath);
    if (capturedKey === "oil_dipstick") setPhotoOilDipstick(storagePath);
    if (capturedKey === "odometer") setPhotoOdoOptional(storagePath);

    // For exterior photos, show success and auto-advance
    if (isExteriorKey(capturedKey)) {
      // Get current photos state (including the one just captured)
      const updatedPhotos = {
        exterior_front: capturedKey === "exterior_front" ? storagePath : photoExteriorFront,
        exterior_rear: capturedKey === "exterior_rear" ? storagePath : photoExteriorRear,
        exterior_driver: capturedKey === "exterior_driver" ? storagePath : photoExteriorDriver,
        exterior_passenger: capturedKey === "exterior_passenger" ? storagePath : photoExteriorPassenger,
      };

      const capturedCount = Object.values(updatedPhotos).filter(Boolean).length;

      // Find next uncaptured exterior photo
      const currentIndex = EXTERIOR_KEYS.indexOf(capturedKey);
      let nextKey: CaptureKey | null = null;

      // First try to find next uncaptured photo after current
      for (let i = currentIndex + 1; i < EXTERIOR_KEYS.length; i++) {
        const key = EXTERIOR_KEYS[i];
        if (!updatedPhotos[key as keyof typeof updatedPhotos]) {
          nextKey = key;
          break;
        }
      }

      // If not found, try from beginning
      if (!nextKey) {
        for (let i = 0; i < currentIndex; i++) {
          const key = EXTERIOR_KEYS[i];
          if (!updatedPhotos[key as keyof typeof updatedPhotos]) {
            nextKey = key;
            break;
          }
        }
      }

      // Show success animation
      setShowSuccess(true);
      if (capturedCount === 4) {
        setSuccessMessage("All exterior photos captured!");
      } else {
        setSuccessMessage(`${VIEW_LABELS[capturedKey]} captured!`);
      }

      // After brief delay, either advance or close
      setTimeout(() => {
        setShowSuccess(false);
        if (capturedCount === 4) {
          // All photos captured - close modal
          closeCameraModal();
        } else if (nextKey) {
          // Advance to next uncaptured photo
          setCurrentCaptureKey(nextKey);
        }
      }, 1200);
    } else {
      // For non-exterior photos, just close
      closeCameraModal();
    }
  }, [photoExteriorFront, photoExteriorRear, photoExteriorDriver, photoExteriorPassenger]);

  async function captureAndUploadFromVideo() {
    if (!videoRef.current) return;
    setUploading(true);
    setError("");

    try {
      const video = videoRef.current;

      const w = video.videoWidth || 0;
      const h = video.videoHeight || 0;

      if (w === 0 || h === 0) {
        setCameraFallback(true);
        throw new Error("Live camera preview failed. Please use Phone Camera instead.");
      }

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;

      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Could not access canvas.");

      ctx.drawImage(video, 0, 0, w, h);

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("Could not create image blob."))),
          "image/jpeg",
          0.9
        );
      });

      const storagePath = await uploadBlobToStorage(blob, currentCaptureKey);
      await upsertPhotoRow(currentCaptureKey, storagePath);

      handleCaptureSuccess(currentCaptureKey, storagePath);
    } catch (e: any) {
      setError(e?.message || "Capture/upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function handleNativeCapture(file: File | null) {
    if (!file) return;

    setUploading(true);
    setError("");

    try {
      const blob = await blobFromFile(file);

      const storagePath = await uploadBlobToStorage(blob, currentCaptureKey);
      await upsertPhotoRow(currentCaptureKey, storagePath);

      handleCaptureSuccess(currentCaptureKey, storagePath);
    } catch (e: any) {
      setError(e?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  function updateChecklistItem(itemKey: string, field: "status" | "note", value: string) {
    setChecklist((prev) => prev.map((item) => (item.item_key === itemKey ? { ...item, [field]: value } : item)));
  }

  async function submitInspection() {
    setSubmitting(true);
    setError("");

    try {
      const overallStatus = computeOverallStatus(checklist);

      const { error: updateError } = await supabase
        .from("vehicle_inspections")
        .update({
          overall_status: overallStatus,
          notes: generalNotes,
          odometer_km: parsedOdometerReading,
          // Marks this inspection as actually finished (checklist + photos),
          // not just started -- see vehicle_inspections.completed_at. Every
          // "is this driver/vehicle up to date" check reads this, not
          // created_at (which is set the moment the wizard opens).
          completed_at: new Date().toISOString(),
        })
        .eq("id", inspectionId);

      if (updateError) throw updateError;

      const checklistRows = checklist.map((item) => ({
        inspection_id: inspectionId,
        category: item.category,
        item_key: item.item_key,
        label: item.label,
        status: item.status,
        note: item.note,
      }));

      const { error: checklistError } = await supabase.from("vehicle_inspection_items").insert(checklistRows);
      if (checklistError) throw checklistError;

      // Wire the reading into the vehicle's "last KM" (only if it's higher
      // than what's already on file -- see recordInspectionOdometer) so it
      // actually feeds RUC/service/tyre-rotation tracking in FleetManagement,
      // not just sit recorded against this one inspection.
      if (parsedOdometerReading && selectedVehicle) {
        await recordInspectionOdometer(selectedVehicle.vehicle_number, parsedOdometerReading, driverName);
      }

      setSubmitSuccess(true);
      setStep(5);
      onInspectionSubmitted?.();
    } catch (err: any) {
      setError("Failed to submit inspection: " + (err.message || "Unknown error"));
    } finally {
      setSubmitting(false);
    }
  }

  const checklistByCategory = useMemo(() => {
    const grouped: Record<string, ChecklistItem[]> = {};
    for (const item of checklist) {
      if (!grouped[item.category]) grouped[item.category] = [];
      grouped[item.category].push(item);
    }
    return grouped;
  }, [checklist]);

  const canGoStep2 = !!photoExteriorFront && !!photoExteriorRear && !!photoExteriorDriver && !!photoExteriorPassenger;
  const parsedOdometerReading = odometerReading.trim() ? Number(odometerReading) : null;
  const odometerReadingValid = parsedOdometerReading !== null && Number.isFinite(parsedOdometerReading) && parsedOdometerReading > 0;
  const canGoStep3 = !!photoOilDipstick && (!requireOdometerReading || odometerReadingValid);

  const overlayForCurrentCapture = useMemo(() => {
    if (!selectedVehicle?.vehicle_type) return "";
    const view = captureKeyToView(currentCaptureKey);
    if (!view) return "";
    return getOverlayUrl(view);
  }, [selectedVehicle?.vehicle_type, currentCaptureKey, vehicleTypes]);

  const currentView = captureKeyToView(currentCaptureKey);
  const currentCal =
    currentView && isExteriorKey(currentCaptureKey) ? calibration[currentView] : { x: 0, y: 0, scale: 1 };

  function saveCalibrationForCurrent() {
    if (!selectedVehicle?.vehicle_type) return;
    if (!currentView) return;
    const vt = selectedVehicle.vehicle_type;
    const key = makeCalKey(vt, currentView);
    localStorage.setItem(key, JSON.stringify(calibration[currentView]));
    setCameraError("Saved calibration for this overlay ✅");
    setTimeout(() => setCameraError(""), 1500);
  }

  function resetCalibrationForCurrent() {
    if (!currentView) return;
    setCalibration((prev) => ({
      ...prev,
      [currentView]: { x: 0, y: 0, scale: 1 },
    }));
  }

  const captureTitle = (() => {
    if (currentCaptureKey === "exterior_front") return "Capture FRONT View";
    if (currentCaptureKey === "exterior_rear") return "Capture REAR View";
    if (currentCaptureKey === "exterior_driver") return "Capture DRIVER View";
    if (currentCaptureKey === "exterior_passenger") return "Capture PASSENGER View";
    if (currentCaptureKey === "oil_dipstick") return "Capture OIL DIPSTICK";
    if (currentCaptureKey === "odometer") return "Capture ODOMETER (Optional)";
    return "Capture Photo";
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-3">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Vehicle Inspection</h1>
        <p className="text-gray-600 mb-6">Complete a pre-shift vehicle inspection to ensure safety and compliance.</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
            <button onClick={() => setError("")} className="float-right text-red-500 hover:text-red-700">
              ×
            </button>
          </div>
        )}


        {/* Vehicle Selection - only show when wizard is not open */}
        {!wizardOpen && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
            <h2 className="text-xl font-semibold text-gray-800 mb-3">Select Vehicle</h2>

            <select
              value={selectedVehicleNumber}
              onChange={(e) => setSelectedVehicleNumber(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
            >
              <option value="">-- Select a vehicle --</option>
              {vehicles.map((v) => (
                <option key={v.vehicle_number} value={v.vehicle_number}>
                  {v.vehicle_number} - {v.make} {v.model} ({v.vehicle_type || "Unknown"})
                </option>
              ))}
            </select>
            {todaysVehicleNumbers.includes(selectedVehicleNumber) && (
              <p className="text-xs text-gray-500 mt-1">
                Defaulted to your rostered vehicle for today -- change it above if you're actually in a different one.
              </p>
            )}

            {selectedVehicle && due && (
              <div className="mt-5">
                <div
                  className={`p-4 rounded-lg ${
                    due.is_due ? "bg-amber-50 border border-amber-200" : "bg-green-50 border border-green-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${due.is_due ? "bg-amber-500" : "bg-green-500"}`} />
                    <div className="flex-1">
                      <p className={`font-medium ${due.is_due ? "text-amber-800" : "text-green-800"}`}>
                        {due.is_due ? "Inspection Due" : "Not Due Yet -- You Can Still Check Now"}
                      </p>
                      <p className="text-sm text-gray-600">{due.reason}</p>
                      {!due.is_due && (
                        <p className="text-sm text-gray-500">You can do a check any day you like -- it just isn't required yet.</p>
                      )}
                      {due.last_inspection_shift_date && (
                        <p className="text-sm text-gray-500">
                          Last inspection: {formatDateTime(due.last_inspection_shift_date)}
                        </p>
                      )}
                      <p className="text-sm text-gray-500">
                        Due every {due.interval_days} day{due.interval_days === 1 ? "" : "s"} or {due.threshold} shifts, whichever comes first
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={startInspection}
                  className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  Start Inspection
                </button>
              </div>
            )}
          </div>
        )}

        {/* WIZARD STEPS */}
        {wizardOpen && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
            {/* Progress indicator */}
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4, 5].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                      step === s
                        ? "bg-blue-600 text-white"
                        : step > s
                        ? "bg-green-500 text-white"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {step > s ? "✓" : s}
                  </div>
                  {s < 5 && (
                    <div className={`w-8 sm:w-16 h-1 mx-1 ${step > s ? "bg-green-500" : "bg-gray-200"}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Exterior Photos */}
            {step === 1 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Step 1: Exterior Photos</h2>
                <p className="text-gray-600 mb-4">
                  Take photos of all four sides of the vehicle. Use the guide overlay to align your shot.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {/* Front */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Front</h3>
                    </div>
                    {photoExteriorFront ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoExteriorFront)}
                          alt="Front"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("exterior_front")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("exterior_front")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                  </div>

                  {/* Rear */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Rear</h3>
                    </div>
                    {photoExteriorRear ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoExteriorRear)}
                          alt="Rear"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("exterior_rear")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("exterior_rear")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                  </div>

                  {/* Driver Side */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Driver Side</h3>
                    </div>
                    {photoExteriorDriver ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoExteriorDriver)}
                          alt="Driver Side"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("exterior_driver")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("exterior_driver")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                  </div>

                  {/* Passenger Side */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Passenger Side</h3>
                    </div>
                    {photoExteriorPassenger ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoExteriorPassenger)}
                          alt="Passenger Side"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("exterior_passenger")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("exterior_passenger")}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => {
                      setWizardOpen(false);
                      setStep(1);
                    }}
                    className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={!canGoStep2}
                    className={`flex-1 font-semibold py-3 rounded-lg transition-colors ${
                      canGoStep2
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Next: Engine Bay
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Engine Bay Photos */}
            {step === 2 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Step 2: Engine Bay Photos</h2>
                <p className="text-gray-600 mb-4">
                  Take a photo of the oil dipstick.
                  {requireOdometerReading
                    ? " The odometer photo is optional, but you must enter the reading."
                    : " Odometer photo is optional."}
                </p>

                <div className="grid grid-cols-2 gap-4">
                  {/* Oil Dipstick */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Oil Dipstick</h3>
                      <p className="text-xs text-gray-500">Required</p>
                    </div>
                    {photoOilDipstick ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoOilDipstick)}
                          alt="Oil Dipstick"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("oil_dipstick")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("oil_dipstick")}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                  </div>

                  {/* Odometer */}
                  <div className="border border-gray-200 rounded-xl p-4">
                    <div className="text-center mb-3">
                      <div className="w-12 h-12 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-2">
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-800">Odometer</h3>
                      <p className="text-xs text-gray-500">Photo optional</p>
                    </div>
                    {photoOdoOptional ? (
                      <div className="relative">
                        <img
                          src={getPublicUrl(photoOdoOptional)}
                          alt="Odometer"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          onClick={() => openCameraFor("odometer")}
                          className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCameraFor("odometer")}
                        className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 rounded-lg transition-colors"
                      >
                        Take Photo
                      </button>
                    )}
                    {requireOdometerReading && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Odometer Reading (km) <span className="text-red-500">Required</span>
                        </label>
                        <input
                          type="number"
                          inputMode="numeric"
                          min={1}
                          value={odometerReading}
                          onChange={(e) => setOdometerReading(e.target.value)}
                          placeholder="e.g. 128450"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(3)}
                    disabled={!canGoStep3}
                    className={`flex-1 font-semibold py-3 rounded-lg transition-colors ${
                      canGoStep3
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Next: Checklist
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Checklist */}
            {step === 3 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Step 3: Safety Checklist</h2>
                <p className="text-gray-600 mb-4">
                  Review each item and mark its status. Add notes for any issues.
                </p>

                <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                  {Object.entries(checklistByCategory).map(([category, items]) => (
                    <div key={category} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 font-semibold text-gray-800">{category}</div>
                      <div className="divide-y divide-gray-100">
                        {items.map((item) => (
                          <div key={item.item_key} className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-gray-800">{item.label}</span>
                              <select
                                value={item.status}
                                onChange={(e) => updateChecklistItem(item.item_key, "status", e.target.value)}
                                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                                style={{ backgroundColor: STATUS_COLOR[item.status] + "20", borderColor: STATUS_COLOR[item.status] }}
                              >
                                {STATUS_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            {priorItemResults[item.item_key] && (
                              <p className="text-xs text-amber-700 mb-2">
                                Last time: {priorItemResults[item.item_key].status}
                                {priorItemResults[item.item_key].note ? ` — ${priorItemResults[item.item_key].note}` : ""}
                              </p>
                            )}
                            {item.status !== "OK" && (
                              <input
                                type="text"
                                placeholder="Add note about this issue..."
                                value={item.note}
                                onChange={(e) => updateChecklistItem(item.item_key, "note", e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setStep(2)}
                    className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep(4)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
                  >
                    Next: Review
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Step 4: Review & Submit</h2>
                <p className="text-gray-600 mb-4">
                  Review your inspection and add any general notes before submitting.
                </p>

                {/* Photos summary */}
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Photos Captured</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {photoExteriorFront && (
                      <img src={getPublicUrl(photoExteriorFront)} alt="Front" className="w-full h-20 object-cover rounded-lg" />
                    )}
                    {photoExteriorRear && (
                      <img src={getPublicUrl(photoExteriorRear)} alt="Rear" className="w-full h-20 object-cover rounded-lg" />
                    )}
                    {photoExteriorDriver && (
                      <img src={getPublicUrl(photoExteriorDriver)} alt="Driver" className="w-full h-20 object-cover rounded-lg" />
                    )}
                    {photoExteriorPassenger && (
                      <img src={getPublicUrl(photoExteriorPassenger)} alt="Passenger" className="w-full h-20 object-cover rounded-lg" />
                    )}
                    {photoOilDipstick && (
                      <img src={getPublicUrl(photoOilDipstick)} alt="Oil" className="w-full h-20 object-cover rounded-lg" />
                    )}
                    {photoOdoOptional && (
                      <img src={getPublicUrl(photoOdoOptional)} alt="Odometer" className="w-full h-20 object-cover rounded-lg" />
                    )}
                  </div>
                </div>

                {odometerReadingValid && (
                  <div className="mb-4 flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 rounded-lg text-sm">
                    <span className="font-semibold">Odometer Reading:</span> {parsedOdometerReading?.toLocaleString()} km
                  </div>
                )}

                {/* Issues summary */}
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Issues Found</h3>
                  {checklist.filter((item) => item.status !== "OK").length === 0 ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                      No issues found - all items OK!
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {checklist
                        .filter((item) => item.status !== "OK")
                        .map((item) => (
                          <div
                            key={item.item_key}
                            className="flex items-center justify-between px-4 py-2 rounded-lg"
                            style={{ backgroundColor: STATUS_COLOR[item.status] + "15" }}
                          >
                            <span className="text-gray-800">{item.label}</span>
                            <span
                              className="text-sm font-medium px-2 py-1 rounded"
                              style={{ backgroundColor: STATUS_COLOR[item.status], color: "white" }}
                            >
                              {item.status}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* General notes */}
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-800 mb-2">General Notes (Optional)</h3>
                  <textarea
                    value={generalNotes}
                    onChange={(e) => setGeneralNotes(e.target.value)}
                    placeholder="Add any additional notes about this inspection..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none"
                    rows={3}
                  />
                </div>

                {/* Overall status preview */}
                <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: STATUS_COLOR[computeOverallStatus(checklist)] + "15" }}>
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: STATUS_COLOR[computeOverallStatus(checklist)] }}
                    />
                    <span className="font-semibold text-gray-800">
                      Overall Status: {computeOverallStatus(checklist)}
                    </span>
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setStep(3)}
                    className="flex-1 border border-gray-300 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    onClick={submitInspection}
                    disabled={submitting}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {submitting ? "Submitting..." : "Submit Inspection"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && submitSuccess && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Inspection Complete!</h2>
                <p className="text-gray-600 mb-6">
                  Your vehicle inspection for {selectedVehicle?.vehicle_number} has been submitted successfully.
                </p>
                <button
                  onClick={() => {
                    setWizardOpen(false);
                    setStep(1);
                    setSelectedVehicleNumber("");
                    setSelectedVehicle(null);
                    setDue(null);
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        )}
      </div>



      {/* CAMERA MODAL */}
      {cameraOpen && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3">
          <div className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{captureTitle}</div>
                <div className="text-xs text-gray-500">
                  Frame: {frameSpec.label}. Enable camera, or use Phone Camera if preview isn't supported.
                </div>
              </div>
              <button onClick={closeCameraModal} className="text-gray-500 hover:text-gray-700 text-xl leading-none">
                ×
              </button>
            </div>

            {/* Photo status indicators - only show for exterior photos */}
            {isExteriorKey(currentCaptureKey) && (
              <div className="bg-gray-50 px-4 py-2 flex justify-center gap-2 border-b">
                {EXTERIOR_KEYS.map((key) => {
                  const photo = getPhotoByKey(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCurrentCaptureKey(key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        key === currentCaptureKey
                          ? "bg-blue-600 text-white"
                          : photo
                            ? "bg-green-500 text-white"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                      }`}
                    >
                      {VIEW_LABELS[key]}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="p-4 overflow-y-auto space-y-4">
              {/* ✅ ALWAYS render stage (video exists regardless of cameraReady) */}
              <div className="relative w-full overflow-hidden rounded-2xl bg-black border">
                <div style={{ paddingTop: aspectToPaddingTop(frameSpec.aspect) }} />
                <div className="absolute inset-0">
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    className="absolute inset-0 w-full h-full"
                    style={{
                      objectFit: "cover",
                      opacity: isStreamActive ? 1 : 0, // hides until stream is real
                      transform: "translateZ(0)",
                      WebkitTransform: "translateZ(0)",
                    }}
                  />

                  {/* Overlay (on top of video) */}
                  {guideVisible && overlayForCurrentCapture && isExteriorKey(currentCaptureKey) && (
                    <img
                      src={overlayForCurrentCapture}
                      alt="overlay"
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        objectFit: "cover",
                        opacity: guideOpacity,
                        transform: `translate(${currentCal.x}px, ${currentCal.y}px) scale(${currentCal.scale})`,
                        transformOrigin: "center center",
                        zIndex: 10,
                      }}
                    />
                  )}

                  {/* If no stream, show a soft placeholder */}
                  {!isStreamActive && !showSuccess && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm z-[5]">
                      Camera preview will appear here
                    </div>
                  )}

                  {/* Success animation overlay */}
                  {showSuccess && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-green-500/90 z-30 animate-pulse">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-4">
                        <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="text-white text-xl font-bold">{successMessage}</div>
                      {capturedExteriorCount < 4 && (
                        <div className="text-white/80 text-sm mt-2">Advancing to next photo...</div>
                      )}
                    </div>
                  )}

                  {/* Debug */}
                  <div className="absolute top-2 left-2 z-20 text-[11px] text-white bg-black/50 px-2 py-1 rounded">
                    w={videoRef.current?.videoWidth ?? 0} h={videoRef.current?.videoHeight ?? 0}
                  </div>

                  {/* Current photo indicator */}
                  {getPhotoByKey(currentCaptureKey) && !showSuccess && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 z-20">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                      Captured
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation buttons - only show for exterior photos */}
              {isExteriorKey(currentCaptureKey) && (
                <div className="flex gap-2">
                  {(() => {
                    const currentIdx = EXTERIOR_KEYS.indexOf(currentCaptureKey);
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            if (currentIdx > 0) {
                              setCurrentCaptureKey(EXTERIOR_KEYS[currentIdx - 1]);
                            }
                          }}
                          disabled={currentIdx === 0}
                          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
                            currentIdx === 0
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400"
                          }`}
                        >
                          ← Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (currentIdx < EXTERIOR_KEYS.length - 1) {
                              setCurrentCaptureKey(EXTERIOR_KEYS[currentIdx + 1]);
                            }
                          }}
                          disabled={currentIdx === EXTERIOR_KEYS.length - 1}
                          className={`flex-1 py-2 rounded-lg font-medium text-sm transition-colors ${
                            currentIdx === EXTERIOR_KEYS.length - 1
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300 active:bg-gray-400"
                          }`}
                        >
                          Next →
                        </button>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Buttons */}
              {!cameraReady ? (
                <>
                  <button
                    onClick={enableCamera}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl"
                    disabled={showSuccess}
                  >
                    Enable Camera
                  </button>

                  <label className="block">
                    <div className="w-full text-center border border-gray-300 rounded-xl py-3 font-semibold text-gray-800 bg-white hover:bg-gray-50 cursor-pointer">
                      Use Phone Camera (Recommended on iPhone/PWA)
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleNativeCapture(e.target.files?.[0] || null)}
                      disabled={uploading || showSuccess}
                    />
                  </label>
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button
                      onClick={captureAndUploadFromVideo}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl"
                      disabled={uploading || showSuccess}
                    >
                      {uploading ? "Uploading..." : "Capture Photo"}
                    </button>
                    <button
                      onClick={closeCameraModal}
                      className="px-4 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50"
                      disabled={uploading || showSuccess}
                    >
                      Stop
                    </button>
                  </div>

                  <label className="block">
                    <div className="w-full text-center border border-gray-300 rounded-xl py-3 font-semibold text-gray-800 bg-white hover:bg-gray-50 cursor-pointer">
                      Use Phone Camera Instead
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="hidden"
                      onChange={(e) => handleNativeCapture(e.target.files?.[0] || null)}
                      disabled={uploading || showSuccess}
                    />
                  </label>
                </>
              )}

              {/* Guide controls */}
              {isExteriorKey(currentCaptureKey) && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-800">Guide</div>
                    <button
                      onClick={() => setGuideVisible((v) => !v)}
                      className="text-sm font-semibold text-blue-700 hover:text-blue-900"
                    >
                      {guideVisible ? "Hide" : "Show"}
                    </button>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                      <span>Opacity</span>
                      <span>{Math.round(guideOpacity * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={guideOpacity}
                      onChange={(e) => setGuideOpacity(parseFloat(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  {isAdmin && currentView && (
                    <div className="pt-2 border-t border-gray-200 space-y-3">
                      <div className="text-sm font-semibold text-gray-800">Calibration (Admin)</div>

                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>X offset</span>
                          <span>{calibration[currentView].x}px</span>
                        </div>
                        <input
                          type="range"
                          min={-200}
                          max={200}
                          step={1}
                          value={calibration[currentView].x}
                          onChange={(e) =>
                            setCalibration((prev) => ({
                              ...prev,
                              [currentView]: { ...prev[currentView], x: parseInt(e.target.value, 10) },
                            }))
                          }
                          className="w-full"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Y offset</span>
                          <span>{calibration[currentView].y}px</span>
                        </div>
                        <input
                          type="range"
                          min={-200}
                          max={200}
                          step={1}
                          value={calibration[currentView].y}
                          onChange={(e) =>
                            setCalibration((prev) => ({
                              ...prev,
                              [currentView]: { ...prev[currentView], y: parseInt(e.target.value, 10) },
                            }))
                          }
                          className="w-full"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                          <span>Scale</span>
                          <span>{calibration[currentView].scale.toFixed(2)}x</span>
                        </div>
                        <input
                          type="range"
                          min={0.5}
                          max={2.0}
                          step={0.01}
                          value={calibration[currentView].scale}
                          onChange={(e) =>
                            setCalibration((prev) => ({
                              ...prev,
                              [currentView]: { ...prev[currentView], scale: parseFloat(e.target.value) },
                            }))
                          }
                          className="w-full"
                        />
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={saveCalibrationForCurrent}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg"
                        >
                          Save
                        </button>
                        <button
                          onClick={resetCalibrationForCurrent}
                          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                        >
                          Reset
                        </button>
                      </div>

                      <div className="text-xs text-gray-500">
                        Calibration saves per vehicle type + view on this device.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {uploading && <div className="text-center text-blue-600 animate-pulse">Uploading...</div>}

              {cameraError && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3 rounded-lg">
                  {cameraError}
                </div>
              )}

              <div className="text-xs text-gray-500">
                If live preview shows black on iPhone Safari/Chrome/PWA, use "Phone Camera".
              </div>

              {/* Done button - only show for exterior photos when navigating */}
              {isExteriorKey(currentCaptureKey) && (
                <button
                  type="button"
                  onClick={closeCameraModal}
                  className="w-full bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white font-semibold py-3 rounded-xl"
                  disabled={showSuccess}
                >
                  Done ({capturedExteriorCount}/4 exterior photos)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
